import {
  CheckThinyIcon,
  CloseIcon,
  GoToIcon,
  IconButton,
  IconButtonColorEnum,
  IconButtonShapeEnumOld,
  IconSizeEnum,
  Tooltip,
  TrashDeleteIcon,
} from '@acceso/design-system';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ListboxSelected } from '@/components/Listbox';
import { LogoSelectionDropdown } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/components/broadcastComponents/LogoSelectionDropdown';
import { PeopleSelectionDropdown } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/components/broadcastComponents/PeopleSelectionDropdown';
import { PresenceTypeEnum } from '@/types/presences';

type PresenceGroupActionsBarProps = {
  presenceType: PresenceTypeEnum;
  onMove: (targetEntityId: string) => void;
  onValidate?: () => void;
  onInvalidate?: () => void;
  onDelete: () => void;
};

const BUTTON_DEFAULTS = {
  color: IconButtonColorEnum.NONE_PRIMARY,
  shape: IconButtonShapeEnumOld.OLD_SQUARED,
};

export function PresenceGroupActionsBar({
  presenceType,
  onMove,
  onValidate,
  onInvalidate,
  onDelete,
}: PresenceGroupActionsBarProps) {
  const { t } = useTranslation();
  const buttonWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownCoords, setDropdownCoords] = useState<{ top: number; left: number } | null>(null);

  const isOpen = dropdownCoords !== null;

  const SelectionDropdown =
    presenceType === PresenceTypeEnum.LOGOS ? LogoSelectionDropdown : PeopleSelectionDropdown;

  const handleToggleMove = () => {
    if (isOpen) {
      setDropdownCoords(null);
      return;
    }
    if (buttonWrapperRef.current) {
      const rect = buttonWrapperRef.current.getBoundingClientRect();
      setDropdownCoords({ top: rect.bottom + 4, left: rect.left });
    }
  };

  const handleMoveSelect = (value: ListboxSelected) => {
    const targetId = value[0] as string;
    if (targetId) onMove(targetId);
    setDropdownCoords(null);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideDropdown = dropdownRef.current?.contains(target);
      const insideButton = buttonWrapperRef.current?.contains(target);
      if (!insideDropdown && !insideButton) {
        setDropdownCoords(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  return (
    <div className="flex flex-row items-center gap-1" data-id="presence-group-actions-bar">
      <div ref={buttonWrapperRef}>
        <Tooltip help={t('Mover presencias a...')} grow>
          <IconButton {...BUTTON_DEFAULTS} onClick={handleToggleMove}>
            <GoToIcon size={IconSizeEnum.MD} />
          </IconButton>
        </Tooltip>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: dropdownCoords!.top,
              left: dropdownCoords!.left,
              zIndex: 9999,
            }}
            className="min-w-[300px] border border-neutral-20 bg-white p-4 shadow-lg"
          >
            <SelectionDropdown onChange={handleMoveSelect} />
          </div>,
          document.body
        )}

      {onValidate && (
        <Tooltip help={t('Marcar como revisado')} grow>
          <IconButton {...BUTTON_DEFAULTS} onClick={onValidate}>
            <CheckThinyIcon size={IconSizeEnum.MD} />
          </IconButton>
        </Tooltip>
      )}
      {onInvalidate && (
        <Tooltip help={t('Anular revisión')} grow>
          <IconButton {...BUTTON_DEFAULTS} onClick={onInvalidate}>
            <CloseIcon size={IconSizeEnum.MD} />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip help={t('Eliminar')} grow>
        <IconButton {...BUTTON_DEFAULTS} onClick={onDelete}>
          <TrashDeleteIcon size={IconSizeEnum.MD} />
        </IconButton>
      </Tooltip>
    </div>
  );
}
